import {
  BacteriaResearch,
  BacteriaResearchSummary,
  ResearchFilter,
  Taxonomy,
} from '@snapi/types';
import { CircleIcon } from '@snapi/icons';
import {
  bacteriaName,
  ResearchCategoryToBackgroundColor,
} from '@snapi/ui-utils';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChild } from '@fortawesome/pro-solid-svg-icons';
import {
  CaretToggle,
  Description,
  DevInfo,
  Percentage,
  Subtitle,
} from '@snapi/luxury';
import { useMemo, useState } from 'react';
import { ResearchFilterUtils } from '@snapi/utils';
import { CardBadge } from '../card-badge/card-badge';
import capitalize from 'lodash.capitalize';
import { ResearchCategoryBadge } from './research-category-badge';
import { CategoryBadge } from './category-badge';
import clsx from 'clsx';

const CardVariants = {
  open: {
    height: 'auto',
    marginTop: 16,
    opacity: 1,
  },
  closed: {
    height: 0,
    marginTop: 0,
    opacity: 0,
  },
};

// const SpeciesResearchHeader = ({
//   research: { name, taxonomy, category },
//   percentage,
//   protective,
//   disruptive,
//   neutral,
//   expanded,
//   onClick,
// }: BacteriaResearch & { expanded: boolean; onClick: () => void }) => (
//   <div className="flex flex-1 cursor-pointer" onClick={onClick}>
//     <CategoryBadge
//       category={category}
//       taxonomy={taxonomy}
//       percentage={percentage}
//       protective={protective}
//       disruptive={disruptive}
//       neutral={neutral}
//     />
//     <div className="flex flex-1 self-center ml-3 md:ml-4">
//       <Subtitle>
//         <span className="flex flex-1 font-semibold">
//           <span className="block truncate">{bacteriaName(name, taxonomy)}</span>
//           &nbsp;&mdash;&nbsp;
//           <Percentage percentage={percentage} />
//         </span>
//       </Subtitle>
//     </div>

//     <CaretToggle open={expanded} />
//   </div>
// );

const BacteriaResearchHeader = ({
  research: { name, taxonomy, category },
  percentage,
  protective,
  disruptive,
  neutral,
  total,
  expanded,
  onClick,
}: BacteriaResearch & {
  total: number;
  expanded: boolean;
  onClick: () => void;
}) => (
  <div className="flex flex-1 cursor-pointer" onClick={onClick}>
    {/* <CategoryBadge
      category={category}
      taxonomy={taxonomy}
      percentage={total}
      protective={protective}
      disruptive={disruptive}
      neutral={neutral}
    /> */}
    <div className="flex flex-1 self-center">
      <span className="flex flex-[3] sm:flex-1">
        <h5>
          {bacteriaName(name, taxonomy)}
          {taxonomy === Taxonomy.family && ' other'}
        </h5>
      </span>
      <span className="flex flex-1 justify-start item-center">
        <h5>
          <Percentage className="flex self-center" percentage={percentage} />
        </h5>
      </span>
    </div>
    <CaretToggle open={expanded} />
  </div>
);

const BacteriaResearchBody = ({
  research: { category, description, condition },
}: BacteriaResearch) => (
  <>
    <div>
      <span className="flex relative my-4">
        <div className="flex justify-center items-center">
          <ResearchCategoryBadge category={category} />
        </div>
        <h6 className="ml-4 self-center">What to know</h6>
      </span>
      <div className="flex flex-1 flex-col gap-3 max-w-[532px]">
        {description.map((description, i) => (
          <span className="flex" key={i}>
            <CircleIcon className="mr-2 mt-2 h-1 w-1" />
            <Description>{description}</Description>
          </span>
        ))}
      </div>
    </div>
    {condition.length > 0 && (
      <div>
        <h6 className="mb-4">Associations</h6>
        <div className="flex flex-1 flex-wrap gap-2">
          {condition.map((condition) => (
            <CardBadge key={condition} good>
              <Subtitle>{capitalize(condition)}</Subtitle>
            </CardBadge>
          ))}
        </div>
      </div>
    )}
  </>
);

export interface BacteriaResearchSummaryCardProps
  extends BacteriaResearchSummary {
  filter: ResearchFilter;
  expanded: boolean;
  onExpand(): void;
}

export const BacteriaResearchSummaryCard: React.FC<
  BacteriaResearchSummaryCardProps
> = ({ filter, bacteria, species, expanded, onExpand }) => {
  const shownChildren = useMemo(
    () =>
      species.filter((child) =>
        ResearchFilterUtils.filterResearch(child, filter)
      ),
    [filter, species]
  );

  const percentage = useMemo(() => {
    if (
      bacteria.research.taxonomy === Taxonomy.family &&
      ResearchFilterUtils.filterResearch(bacteria, filter)
    ) {
      return bacteria.other + bacteria.unclassified;
    }
    return ResearchFilterUtils.overlapPercentage(bacteria, species, filter);
  }, [species, filter, bacteria]);

  const [selectedSpecies, setSelectedSpecies] = useState<null | string>(null);

  const other = useMemo(
    () =>
      ResearchFilterUtils.filterResearch(bacteria, filter)
        ? bacteria.other + bacteria.unclassified
        : 0,
    [filter, bacteria]
  );

  return (
    <div className="bg-white relative rounded-[10px] py-4 px-3 max-w-[610px] xl:max-w-[630px]">
      <BacteriaResearchHeader
        {...bacteria}
        total={bacteria.percentage}
        percentage={bacteria.percentage}
        expanded={expanded}
        onClick={onExpand}
      />
      <DevInfo>
        {species.length > 0 && (
          <small className="absolute top-2 right-2 font-mono">
            {species.length} / {shownChildren.length}
            <FontAwesomeIcon icon={faChild} className="h-3 w-3" />
          </small>
        )}
      </DevInfo>
      <AnimatePresence>
        <motion.div
          initial={
            expanded
              ? { height: 'auto', paddingTop: 0 }
              : { height: 0, paddingTop: 0 }
          }
          animate={expanded ? 'open' : 'closed'}
          transition={{ duration: 0.4 }}
          style={{ overflow: 'hidden' }}
          className="relative flex flex-col gap-7 overflow-visible"
          variants={CardVariants}
        >
          <div className="absolute top-0 -left-3 -right-3 border-b border-gray-soft" />
          <BacteriaResearchBody {...bacteria} />
          {/* {(shownChildren.length > 0 || other > 0) && (
            <div>
              <h6 className="mb-4">Species level</h6>
              <div className="flex flex-col gap-2">
                {shownChildren.map((species) => {
                  const expanded = selectedSpecies === species.research.name;
                  return (
                    <div
                      key={species.research.name}
                      className="rounded-[10px] border border-body p-3"
                    >
                      <SpeciesResearchHeader
                        {...species}
                        expanded={expanded}
                        onClick={() => {
                          if (selectedSpecies === species.research.name) {
                            setSelectedSpecies(null);
                          } else {
                            setSelectedSpecies(species.research.name);
                          }
                        }}
                      />
                      <AnimatePresence>
                        <motion.div
                          initial={
                            expanded
                              ? { height: 'auto', paddingTop: 0 }
                              : { height: 0, paddingTop: 0 }
                          }
                          animate={expanded ? 'open' : 'closed'}
                          transition={{ duration: 0.4 }}
                          style={{ overflow: 'hidden' }}
                          className="relative flex flex-col gap-7 overflow-visible"
                          variants={CardVariants}
                        >
                          <div className="absolute top-0 -left-3 -right-3 border-b border-gray-soft" />
                          <BacteriaResearchBody {...species} />
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  );
                })}
                {other > 0 && (
                  <div className="bg-white rounded-[10px] border border-gray-soft py-4 px-3">
                    <div className="flex flex-1">
                      <span
                        className={clsx(
                          'h-4 w-4 flex self-center rounded-full',
                          ResearchCategoryToBackgroundColor[
                            bacteria.research.category
                          ]
                        )}
                      />
                      <div className="flex flex-1 self-center relative ml-3 md:ml-4">
                        <Subtitle>
                          <span className="font-semibold">
                            {bacteriaName(
                              `${bacteria.research.name} other`,
                              Taxonomy.species
                            )}
                            &nbsp;&mdash;&nbsp;
                            <Percentage percentage={other} />
                          </span>
                        </Subtitle>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )} */}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
